import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  IconButton,
  SelectChangeEvent,
  Stack,
  Tooltip,
  useTheme
} from "@mui/material";
import styles from "./styles.module.scss";
import { strings } from "../../../localization/LocalizedStrings";
import { RightDetailListItem } from "../../RightDetailListItem";
import { SwitchListItem } from "../../SwitchListItem";
import { UpdateEmailDialog } from "../../UpdateEmailDialog";
import { UpdatePasswordDialog } from "../../UpdatePasswordDialog";
import { useGetUserProfile, useModifyUserProfile } from "../../../hooks/userProfiles";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { AlertDialog } from "../../AlertDialog";
import { ModifyGenericAttributeDialog } from "../../attributes/ModifyGenericAttributeDialog";
import { SelectListItem } from "../../SelectListItem";
import { FreeTextListItem } from "../../FreeTextListItem";
import { ModifyFreeTextAttributeDialog } from "../../attributes/ModifyFreeTextAttributeDialog";
import { SelectChipsListItem } from "../../SelectChipsListItem";
import { toast } from "react-toastify";
import { RoutesBuilder } from "../../../models/RoutesBuilder";
import { useNavigate } from "react-router";
import { UserUtilities } from "../../../models/UserUtilities";
import { SubscriptionsDialog } from "../../subscriptions/SubscriptionsDialog";
import { MySubscriptionDialog } from "../../subscriptions/MySubscriptionDialog";
import { ResourceLimits, UserRole } from "../../../hooks/users";
import {
  usePublicUserProfile,
  useSessionManager,
  useUser,
  useUserProfile
} from "../../../hooks/session";
import {
  useCurrencies,
  useCurrenciesManager
} from "../../../hooks/currencies/useCurrenciesManager";
import { useGetCurrencies } from "../../../hooks/currencies";
import { format } from "date-fns";
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from "react-resizable-panels";
import { useIsMobile } from "../../../hooks/isMobile";
import { PublicCollectionsGrid } from "../../marketplace/PublicCollectionsGrid";
import { IPublicCollection } from "../../../hooks/collections";
import { useCurrentPublicCollection } from "../../../hooks/collections/useCollectionManager";
import { useGetShareLink } from "../../../hooks/share";
import { useGetPublicCollections } from "../../../hooks/collections/useGetPublicCollections";
import { useGetUser } from "../../../hooks/users/useGetUser";
import { ImageDropzone } from "../../ImageDropzone";
import { ImageCache } from "../../../models/ImageCache";
import { useGetImage, useUploadImage } from "../../../hooks/images";
import { PreferencesDialog } from "../../PreferencesDialog";
import { useGetResourceLimits } from "../../../hooks/users/useGetResourceLimits";
import { ResourceLimitListItem } from "../ResourceLimitListItem";
import { ProductType } from "../../../hooks/products/interfaces";
import { ContainedButton } from "../../ContainedButton";
import { ProductPurchasesDialog } from "../../products/ProductPurchasesDialog";
import { Icon, IconType } from "../../../icons";
import { PiperDivider } from "../../PiperDivider";
import { PasskeysDialog } from "../../passkeys/PasskeysDialog";
import { useResettableDialog } from "../../../hooks/resettableDialog/useResettableDialog";

export const UserProfileDetail: FunctionComponent<IUserProfileDetailProps> = ({ setIsLoading }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { modifyUserProfile } = useModifyUserProfile();
  const [currentUser] = useUser.useState();
  const { getImage } = useGetImage();
  const { uploadImage } = useUploadImage();
  const [image, setImage] = useState();
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useUserProfile.useState();
  const { getUser } = useGetUser();
  const { getUserProfile } = useGetUserProfile();
  const { closeSession } = useSessionManager();
  const { getCurrencies } = useGetCurrencies();
  const [currencies] = useCurrencies.useState();
  const { getCurrency, getCurrenciesCodeList, getCurrenciesStringList } = useCurrenciesManager();
  const [, setCurrentPublicUserProfile] = usePublicUserProfile.useState();
  const [, setCurrentPublicCollection] = useCurrentPublicCollection.useState();
  const { getShareLink } = useGetShareLink();
  const [isPurchaseBundleDialogOpen, setIsPurchaseBundleDialogOpen] = useState(false);
  const [isLoadingPublicCollections, setIsLoadingPublicCollections] = useState(false);
  const [publicCollections] = useGetPublicCollections(
    currentUserProfile?.userId,
    setIsLoadingPublicCollections,
    true
  );
  const [shouldOpenFileDialog, setShouldOpenFileDialog] = useState(false);
  const [isUpdateUsernameDialogOpen, setIsUpdateUsernameDialogOpen] = useState(false);
  const [isPreferencesDialogOpen, setIsPreferencesDialogOpen] = useState(false);
  const [isUpdateEmailDialogOpen, setIsUpdateEmailDialogOpen] = useState(false);
  const [isUpdatePasswordDialogOpen, setIsUpdatePasswordDialogOpen] = useState(false);
  const [isPasskeysDialogOpen, setIsPasskeysDialogOpen] = useState(false);
  const [isSubscriptionsDialogOpen, setIsSubscriptionsDialogOpen] = useState(false);
  const [isMySubscriptionDialogOpen, setIsMySubscriptionDialogOpen] = useState(false);
  const {
    state: noColorNameDialogState,
    key: noColorNameDialogKey,
    openAdd: openNoColorNameDialog,
    close: closeNoColorNameDialog
  } = useResettableDialog();
  const [isBioDialogOpen, setIsBioDialogOpen] = useState(false);
  const { getResourceLimits } = useGetResourceLimits();
  const [resourceLimits, setResourceLimits] = useState<ResourceLimits>();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const placeholderAvatar = new URL("../../../images/generic_sleeve.jpg", import.meta.url).href;
  useEffect(() => {
    if (!currentUser) {
      getUser().then();
    }
    if (!currentUserProfile) {
      getUserProfile().then();
    }
  }, []);
  useEffect(() => {
    if (currentUser?.role == UserRole.guest) {
      navigate(RoutesBuilder.home());
    }
  }, [currentUser]);
  const fetchImage = async () => {
    setImage(undefined);
    if (!currentUserProfile || !currentUserProfile.hasImage) return;
    const response = await getImage({
      name: currentUserProfile.uuid,
      width: ImageCache.smallAvatarImageSize,
      height: ImageCache.smallAvatarImageSize
    });
    if (StatusCodesHelper.isSuccessful(response.status)) {
      setImage(response.body);
    }
  };
  const getLimits = async () => {
    const { status, body } = await getResourceLimits();
    if (StatusCodesHelper.isSuccessful(status)) {
      setResourceLimits(body);
    }
  };
  useEffect(() => {
    fetchImage().then();
    getLimits().then();
  }, [currentUserProfile]);
  const showCollection = async (publicCollection: IPublicCollection) => {
    setIsLoading(true);
    const { status, body } = await getShareLink({
      collectionId: publicCollection.collection.uuid
    });
    if (StatusCodesHelper.isSuccessful(status)) {
      setCurrentPublicUserProfile(undefined);
      setCurrentPublicCollection(undefined);
      navigate(body.relativeShareLink);
    }
    setIsLoading(false);
  };
  const updateProfile = async (newValue: Record<string, any>) => {
    setIsLoading(true);
    const { status, body } = await modifyUserProfile(newValue);
    if (StatusCodesHelper.isSuccessful(status)) {
      toast.success(strings.generic_update_success);
      setCurrentUserProfile(body);
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
    setIsLoading(false);
  };
  const handleCurrencyChange = async (event: SelectChangeEvent) => {
    const code = event.target.value as string;
    const newValue = getCurrency(code);
    setIsLoading(true);
    const { status, body } = await modifyUserProfile({
      uuid: currentUserProfile?.uuid,
      currency: newValue
    });
    if (StatusCodesHelper.isSuccessful(status)) {
      toast.success(strings.generic_update_success);
      setCurrentUserProfile(body);
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
    setIsLoading(false);
  };
  const handleOrderNewAlbumsChange = (_event: React.MouseEvent<HTMLElement>, newValue: boolean) => {
    if (newValue !== null) {
      updateProfile({
        uuid: currentUserProfile?.uuid,
        orderNewAlbums: newValue
      }).then();
    }
  };
  const handleShouldShowImpactsDescriptionChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: boolean
  ) => {
    if (newValue !== null) {
      updateProfile({
        uuid: currentUserProfile?.uuid,
        shouldShowImpactDescriptions: newValue
      }).then();
    }
  };
  const handleShouldShowAlbumIdChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: boolean
  ) => {
    if (newValue !== null) {
      updateProfile({
        uuid: currentUserProfile?.uuid,
        shouldShowAlbumId: newValue
      }).then();
    }
  };
  const handleShowDecimalsChange = (_event: React.MouseEvent<HTMLElement>, newValue: boolean) => {
    if (newValue !== null) {
      updateProfile({
        uuid: currentUserProfile?.uuid,
        showDecimals: newValue
      }).then();
    }
  };
  const handleReceiveEmailNotificationsChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: boolean
  ) => {
    if (newValue !== null) {
      updateProfile({
        uuid: currentUserProfile?.uuid,
        receiveEmailNotifications: newValue
      }).then();
    }
  };
  const handleShow3DModelChange = (_event: React.MouseEvent<HTMLElement>, newValue: boolean) => {
    if (newValue !== null) {
      updateProfile({
        uuid: currentUserProfile?.uuid,
        show3DModel: newValue
      }).then();
    }
  };
  useEffect(() => {
    if (!currencies) {
      getCurrencies().then();
    }
  }, []);
  const profileList = () => (
    <div className={styles.list}>
      <div className={styles.avatarContainer}>
        <Avatar
          className={styles.avatar}
          sx={{
            marginLeft: 2,
            marginBottom: 2,
            borderWidth: 2,
            borderColor: theme.palette.primary.main,
            borderStyle: "solid"
          }}
        >
          <ImageDropzone
            className={styles.dropzone}
            disabled={isLoadingImage}
            onImageChanged={async file => {
              if (!currentUserProfile) return;
              setIsLoadingImage(true);
              const newThumbnail = await uploadImage(
                { name: currentUserProfile.uuid, file },
                ImageCache.bigAvatarImageSize,
                ImageCache.bigAvatarImageSize,
                ImageCache.smallAvatarImageSize,
                ImageCache.smallAvatarImageSize
              );
              if (newThumbnail) {
                setCurrentUserProfile({
                  ...currentUserProfile,
                  hasImage: true,
                  thumbnail: newThumbnail
                });
                await fetchImage();
              }
              setIsLoadingImage(false);
            }}
            placeholderImage={placeholderAvatar}
            isLoading={isLoadingImage}
            shouldOpenFileDialog={shouldOpenFileDialog}
            initialImage={
              image
                ? `data:image/png;base64,${image}`
                : currentUserProfile?.thumbnail
                  ? `data:image/png;base64,${currentUserProfile?.thumbnail}`
                  : undefined
            }
          />
          <Stack className={styles.hoverButtonsStack} direction={"row"} spacing={1}>
            <span>
              <Tooltip title={strings.edit_avatar} arrow disableInteractive>
                <IconButton
                  className={styles.hoverButton}
                  disabled={isLoadingImage}
                  style={{ backgroundColor: theme.palette.colors?.counterBackground }}
                  onClick={() => setShouldOpenFileDialog(state => !state)}
                >
                  <Icon type={IconType.EditSquare} />
                </IconButton>
              </Tooltip>
            </span>
          </Stack>
        </Avatar>
      </div>
      <Card
        elevation={5}
        className={isMobile ? styles.sectionMobile : styles.section}
        style={{ backgroundColor: theme.palette.background.default }}
      >
        <RightDetailListItem
          key={"update_username"}
          iconType={IconType.AccountBox}
          title={strings.username_title}
          detail={currentUserProfile?.username ?? ""}
          onClick={() => {
            setIsUpdateUsernameDialogOpen(true);
          }}
        />
        <PiperDivider />
        <RightDetailListItem
          key={"update_email"}
          iconType={IconType.ContactMail}
          title={strings.email_title}
          leftSubtitle={currentUser?.email ?? ""}
          onClick={() => {
            setIsUpdateEmailDialogOpen(true);
          }}
        />
        <PiperDivider />
        <RightDetailListItem
          key={"update_password"}
          iconType={IconType.ContactEmergency}
          title={strings.update_password_title}
          detail={"● ● ● ● ●"}
          onClick={() => {
            setIsUpdatePasswordDialogOpen(true);
          }}
        />
        <PiperDivider />
        <RightDetailListItem
          key={"passkeys"}
          iconType={IconType.Passkey}
          title={strings.passkeys_title}
          onClick={() => {
            setIsPasskeysDialogOpen(true);
          }}
        />
        <PiperDivider />
        {currentUserProfile && (
          <RightDetailListItem
            key={"user_since"}
            iconType={IconType.Today}
            title={strings.user_since_title}
            detail={format(currentUserProfile.createdUnixTimestamp * 1000, "d MMMM yyyy", {
              locale: strings.fnsLocale
            })}
          />
        )}
        <PiperDivider />
        <RightDetailListItem
          key={"update_role"}
          iconType={IconType.BusinessCenter}
          title={strings.update_role_title}
          detail={UserUtilities.roleName(currentUser?.role)}
          onClick={() => {
            if (currentUser?.role == UserRole.common) {
              setIsSubscriptionsDialogOpen(true);
            } else {
              setIsMySubscriptionDialogOpen(true);
            }
          }}
        />
      </Card>
      <Card
        elevation={5}
        className={isMobile ? styles.sectionMobile : styles.section}
        style={{ backgroundColor: theme.palette.background.default }}
      >
        <FreeTextListItem
          iconType={IconType.HistoryEdu}
          title={strings.bio_title}
          detail={currentUserProfile?.bio ?? "--"}
          maxLines={6}
          onClick={() => {
            setIsBioDialogOpen(true);
          }}
        />
      </Card>
      <Card
        elevation={5}
        className={isMobile ? styles.sectionMobile : styles.section}
        style={{ backgroundColor: theme.palette.background.default }}
      >
        <SelectListItem
          key={"currency"}
          iconType={IconType.Paid}
          title={strings.currency_title}
          labels={getCurrenciesStringList()}
          values={getCurrenciesCodeList()}
          value={currentUserProfile?.currency.code}
          handleChange={handleCurrencyChange}
        />
        <PiperDivider />
        <SwitchListItem
          key={"receive_email_notifications"}
          className={styles.switchListItem}
          iconType={IconType.Mail}
          title={strings.settings_receive_email_notifications}
          labels={[strings.no, strings.yes]}
          values={[false, true]}
          value={currentUserProfile?.receiveEmailNotifications}
          fullWidth={false}
          handleChange={handleReceiveEmailNotificationsChange}
        />
        <PiperDivider />
        <SwitchListItem
          key={"show_decimals"}
          className={styles.switchListItem}
          iconType={IconType.DecimalIncrease}
          title={strings.settings_show_decimals}
          labels={[strings.no, strings.yes]}
          values={[false, true]}
          value={currentUserProfile?.showDecimals}
          fullWidth={false}
          handleChange={handleShowDecimalsChange}
        />
        <PiperDivider />
        <SwitchListItem
          key={"show_3d_model"}
          className={styles.switchListItem}
          iconType={IconType.ThreeDimensionalRotation}
          title={strings.settings_show_3d_model}
          labels={[strings.no, strings.yes]}
          values={[false, true]}
          value={currentUserProfile?.show3DModel}
          fullWidth={false}
          handleChange={handleShow3DModelChange}
        />
        <PiperDivider />
        <SwitchListItem
          key={"order_new_albums"}
          className={styles.switchListItem}
          iconType={IconType.FormatListNumbered}
          title={strings.settings_order_new_albums}
          labels={[strings.no, strings.yes]}
          values={[false, true]}
          value={currentUserProfile?.orderNewAlbums}
          fullWidth={false}
          handleChange={handleOrderNewAlbumsChange}
          helpHref={`${window.location.protocol}//${
            window.location.host
          }${RoutesBuilder.documentation.home()}${RoutesBuilder.documentation.album.orderAlbums()}`}
        />
        <PiperDivider />
        <SwitchListItem
          key={"show_rating_description"}
          className={styles.switchListItem}
          iconType={IconType.Star}
          title={strings.settings_should_show_impact_descriptions}
          labels={[strings.no, strings.yes]}
          values={[false, true]}
          value={currentUserProfile?.shouldShowImpactDescriptions}
          fullWidth={false}
          handleChange={handleShouldShowImpactsDescriptionChange}
        />
        <PiperDivider />
        <SwitchListItem
          key={"show_album_id"}
          className={styles.switchListItem}
          iconType={IconType.Pin}
          title={strings.settings_should_show_album_id}
          labels={[strings.no, strings.yes]}
          values={[false, true]}
          value={currentUserProfile?.shouldShowAlbumId}
          fullWidth={false}
          handleChange={handleShouldShowAlbumIdChange}
        />
        <PiperDivider />
        <SelectChipsListItem
          iconType={IconType.Palette}
          className={styles.switchListItem}
          title={strings.no_color_names_title}
          chips={currentUserProfile?.noColorNames?.map(color => ({
            id: color,
            name: color
          }))}
          helpHref={`${window.location.protocol}//${
            window.location.host
          }${RoutesBuilder.documentation.home()}${RoutesBuilder.documentation.userProfile(
            "colors"
          )}`}
          onAdd={openNoColorNameDialog}
          onDelete={index => {
            const noColorNames = [...(currentUserProfile?.noColorNames ?? [])];
            if (index !== undefined && index >= 0) {
              noColorNames?.splice(index, 1);
            }
            updateProfile({
              uuid: currentUserProfile?.uuid,
              noColorNames: noColorNames
            }).then();
          }}
        />
      </Card>
      <Card
        elevation={5}
        className={isMobile ? styles.sectionMobile : styles.section}
        style={{ backgroundColor: theme.palette.background.default }}
      >
        <RightDetailListItem
          key={"Preferences"}
          iconType={IconType.Settings}
          title={strings.option_preferences}
          onClick={() => {
            setIsPreferencesDialogOpen(true);
          }}
        />
        <PiperDivider />
        <RightDetailListItem
          key={"CloseSession"}
          iconType={IconType.Logout}
          title={strings.option_close_session}
          onClick={() => {
            closeSession();
            navigate(RoutesBuilder.signIn());
          }}
        />
      </Card>
      <ModifyGenericAttributeDialog
        title={strings.username_title}
        description={strings.modify_username_message}
        placeholder={strings.username_title}
        missingText={strings.field_missing}
        value={currentUserProfile?.username}
        canClear={false}
        open={isUpdateUsernameDialogOpen}
        onClose={() => setIsUpdateUsernameDialogOpen(false)}
        isNumber={false}
        onChange={newValue => {
          updateProfile({
            uuid: currentUserProfile?.uuid,
            username: newValue
          }).then();
        }}
      />
      <UpdateEmailDialog open={isUpdateEmailDialogOpen} setOpen={setIsUpdateEmailDialogOpen} />
      <UpdatePasswordDialog
        open={isUpdatePasswordDialogOpen}
        setOpen={setIsUpdatePasswordDialogOpen}
      />
      <PasskeysDialog open={isPasskeysDialogOpen} setOpen={setIsPasskeysDialogOpen} />
      <ModifyFreeTextAttributeDialog
        title={strings.bio_title}
        description={strings.bio_message}
        placeholder={strings.bio_title}
        open={isBioDialogOpen}
        onClose={() => setIsBioDialogOpen(false)}
        value={currentUserProfile?.bio}
        onChange={newValue => {
          updateProfile({
            uuid: currentUserProfile?.uuid,
            bio: newValue
          }).then();
        }}
        onClearValue={() => {
          updateProfile({
            uuid: currentUserProfile?.uuid,
            bio: ""
          }).then();
        }}
      />
      <ModifyGenericAttributeDialog
        key={noColorNameDialogKey}
        title={strings.no_color_name_title}
        description={strings.no_color_name_message}
        placeholder={strings.disc_color_placeholder}
        missingText={strings.field_missing}
        canClear={false}
        open={noColorNameDialogState.mode !== "closed"}
        onClose={closeNoColorNameDialog}
        isNumber={false}
        onChange={newValue => {
          const noColorNames = [...(currentUserProfile?.noColorNames ?? [])];
          noColorNames?.push(newValue);
          updateProfile({
            uuid: currentUserProfile?.uuid,
            noColorNames: noColorNames
          }).then();
        }}
      />
      <SubscriptionsDialog
        isOpen={isSubscriptionsDialogOpen}
        setIsOpen={setIsSubscriptionsDialogOpen}
      />
      <MySubscriptionDialog
        isOpen={isMySubscriptionDialogOpen}
        setIsOpen={setIsMySubscriptionDialogOpen}
      />
      <PreferencesDialog
        isOpen={isPreferencesDialogOpen}
        setIsOpen={setIsPreferencesDialogOpen}
        canRemoveAccount
      />
      <AlertDialog message={alertMessage} open={isAlertOpen} setOpen={setIsAlertOpen} />
    </div>
  );
  const resourceLimitsList = () => (
    <div>
      <ResourceLimitListItem
        productType={ProductType.ALBUMS}
        title={strings.albums_plural_title}
        detailBlurred={!resourceLimits}
        currentAmount={resourceLimits?.albumCount}
        totalAmount={resourceLimits?.limitAlbums}
      />
      <PiperDivider />
      <ResourceLimitListItem
        productType={ProductType.ARTISTS}
        title={strings.attribute_artists_title}
        detailBlurred={!resourceLimits}
        currentAmount={resourceLimits?.artistCount}
        totalAmount={resourceLimits?.limitArtists}
      />
      <PiperDivider />
      <ResourceLimitListItem
        productType={ProductType.LABELS}
        title={strings.attribute_labels_title}
        detailBlurred={!resourceLimits}
        currentAmount={resourceLimits?.labelCount}
        totalAmount={resourceLimits?.limitLabels}
      />
      <PiperDivider />
      <ResourceLimitListItem
        productType={ProductType.ALBUM_FORMATS}
        title={strings.attribute_album_formats_title}
        detailBlurred={!resourceLimits}
        currentAmount={resourceLimits?.albumFormatCount}
        totalAmount={resourceLimits?.limitAlbumFormats}
      />
      <PiperDivider />
      <ResourceLimitListItem
        productType={ProductType.ALBUM_TYPES}
        title={strings.attribute_album_types_title}
        detailBlurred={!resourceLimits}
        currentAmount={resourceLimits?.albumTypeCount}
        totalAmount={resourceLimits?.limitAlbumTypes}
      />
      <PiperDivider />
      <ResourceLimitListItem
        productType={ProductType.TAGS}
        title={strings.tags_title}
        detailBlurred={!resourceLimits}
        currentAmount={resourceLimits?.tagCount}
        totalAmount={resourceLimits?.limitTags}
      />
      <PiperDivider />
      <ResourceLimitListItem
        productType={ProductType.STORES}
        title={strings.attribute_stores_title}
        detailBlurred={!resourceLimits}
        currentAmount={resourceLimits?.storeCount}
        totalAmount={resourceLimits?.limitStores}
      />
      <PiperDivider />
      <ResourceLimitListItem
        productType={ProductType.SELL_VALUES}
        title={strings.attribute_sell_values_title}
        detailBlurred={!resourceLimits}
        currentAmount={resourceLimits?.sellValueCount}
        totalAmount={resourceLimits?.limitSellValues}
      />
      <PiperDivider />
      <ResourceLimitListItem
        productType={ProductType.EXCHANGE_RATES}
        title={strings.attribute_exchange_rates_title}
        detailBlurred={!resourceLimits}
        currentAmount={resourceLimits?.exchangeRateCount}
        totalAmount={resourceLimits?.limitExchangeRates}
      />
      <PiperDivider />
      <ResourceLimitListItem
        productType={ProductType.COLLECTIONS}
        title={strings.collections_title}
        detailBlurred={!resourceLimits}
        currentAmount={resourceLimits?.collectionCount}
        totalAmount={resourceLimits?.limitCollections}
      />
      <PiperDivider />
      <ResourceLimitListItem
        productType={ProductType.SHELVINGS}
        title={strings.shelvings_title}
        detailBlurred={!resourceLimits}
        currentAmount={resourceLimits?.shelvingCount}
        totalAmount={resourceLimits?.limitShelvings}
      />
    </div>
  );
  return (
    <Card
      className={styles.mainContainer}
      style={{ backgroundColor: theme.palette.background.default }}
    >
      <PanelGroup className={styles.mainDiv} orientation={"horizontal"}>
        {!isMobile && currentUserProfile && (
          <Panel
            id={"left"}
            minSize={"40"}
            defaultSize={"50"}
            maxSize={"60"}
            style={{ display: "flex", overflow: "auto" }}
          >
            {profileList()}
          </Panel>
        )}
        {!isMobile && (
          <PanelResizeHandle className={styles.resizeHandle}>
            <Icon
              type={IconType.DragIndicator}
              style={{ alignSelf: "center", color: theme.palette.secondary.main, fontSize: 14 }}
            />
          </PanelResizeHandle>
        )}
        <Panel id={"right"} className={isMobile ? styles.graphsDivMobile : styles.graphsDiv}>
          {isMobile && currentUserProfile && profileList()}
          {currentUserProfile && (
            <div className={isMobile ? styles.detailMobile : undefined}>
              <Stack direction={"row"}>
                <h4 className={styles.publicCollectionsTitle}>{strings.resource_limits_title}</h4>
                <Box flexGrow={1} />
                <ContainedButton onClick={() => setIsPurchaseBundleDialogOpen(true)}>
                  <Stack direction={"row"}>
                    <Icon type={IconType.LocalMall} />
                    <Box style={{ width: "8px" }} />
                    {strings.bundles_title}
                  </Stack>
                </ContainedButton>
              </Stack>
              <Card
                elevation={5}
                className={styles.limitsCard}
                style={{ backgroundColor: theme.palette.background.default }}
              >
                {resourceLimitsList()}
              </Card>
              <h4 className={styles.publicCollectionsTitle}>{strings.public_collections_title}</h4>
              <Card elevation={5} className={styles.publicCollectionsCard}>
                <PublicCollectionsGrid
                  isLoading={isLoadingPublicCollections}
                  publicCollections={publicCollections.map(publicCollection => ({
                    collection: publicCollection.collection,
                    userProfile: publicCollection.userProfile
                  }))}
                  onCollectionSelected={showCollection}
                  columnWidth={isMobile ? 12 : 6}
                  skeletonCount={4}
                  showUser={false}
                />
              </Card>
            </div>
          )}
        </Panel>
      </PanelGroup>
      <ProductPurchasesDialog
        open={isPurchaseBundleDialogOpen}
        setOpen={setIsPurchaseBundleDialogOpen}
        productType={ProductType.BUNDLES}
      />
    </Card>
  );
};

export interface IUserProfileDetailProps {
  setIsLoading: (isLoading: boolean) => void;
}
